<script lang="ts">
  import FullName from "../components/FullName.svelte";
  import { PALETTE, isTestsGroup, firstActivity, owedTrend, reminderMessage, trendDates, peopleTotals, groupSource, personOverview, visiblePeople as filterPeople, paidStatus, paidProgress, type Payment, type PersonSummary, type StatementEntry } from "@split/core";
  import PersonCard from "../components/PersonCard.svelte";
  import { groupSentence, type CardViewer } from "../lib/personCard.ts";
  import { fill, shows } from "../settings/index.ts";
  import * as T from "../settings/people.ts";
  import * as A from "../settings/amounts.ts";
  import { groupPath, router } from "../lib/router.svelte.ts";
  import Sparkline from "../components/charts/Sparkline.svelte";
  import Swatch from "../components/Swatch.svelte";
  import PaymentEditor from "../components/PaymentEditor.svelte";
  import { app } from "../lib/store.svelte.ts";
  import { nav } from "../lib/nav.svelte.ts";
  import { colorOf } from "../lib/colors.ts";
  import { api, NetworkError } from "../lib/api.ts";
  import { formatCents, formatMicro, shortDate, shortDateTime, tidyName } from "../lib/format.ts";

  const L = $derived(app.ledger);
  const S = $derived(app.viewState);
  let open = $state<string | null>(null);
  let paying = $state<{ personId: string; amountCents: number } | null>(null);
  /** An existing payment being changed (from someone's history). */
  let editingPayment = $state<Payment | null>(null);
  /** One person here, the same card as on main People: this group is their only source. */
  const overviewOf = (p: PersonSummary) => {
    const src = groupSource(S, L, p.personId, groupName, app.groupId ?? "");
    return personOverview(src ? [src] : []);
  };
  /** Whose eyes: yours, a member's own row, or a member looking at someone else's. */
  const viewerOf = (p: PersonSummary): CardViewer =>
    !app.memberSession
      ? { side: "owner" }
      : { side: app.memberSession.personId === p.personId ? "self" : "member", treasurer: treasurerName };
  /** A payment line on someone's history: change it. */
  function openEntry(e: StatementEntry) {
    const pay = S.payments[e.id];
    if (pay) editingPayment = pay;
  }
  let newName = $state("");
  let newColor = $state<string>("");

  const owing = $derived(L.people.filter((p) => p.status === "owes"));
  const owed = $derived(L.people.filter((p) => p.status === "owed"));
  const totals = $derived(peopleTotals(L.people));
  const owedSum = $derived(totals.owedSum);
  const oweSum = $derived(totals.oweSum);
  const usedColors = $derived(new Set(Object.values(S.people).map((p) => p.color)));
  const freeColors = $derived(PALETTE.filter((c) => !usedColors.has(c)));
  // Search and sort, so a group of hundreds stays easy to scan.
  let q = $state("");
  let sort = $state<"order" | "owes" | "name">("order");
  const visiblePeople = $derived(filterPeople(L.people, (id) => !!S.people[id]?.archived, q, sort));

  $effect(() => {
    if (!newColor || usedColors.has(newColor)) newColor = freeColors[0] ?? "Gray";
  });


  const treasurerName = $derived(S.settings.ownerPersonId ? (S.people[S.settings.ownerPersonId]?.name ?? "the treasurer") : "the treasurer");
  const progress = paidProgress;

  async function copy(p: PersonSummary) {
    const text = reminderMessage(p);
    try {
      await navigator.clipboard.writeText(text);
      app.toast(`Copied the message for ${p.name}`);
    } catch {
      app.toast("Couldn't copy. Long-press the message to copy it instead.", "error");
      open = p.personId;
    }
  }

  // ---- accounts: who has joined, invites, previews
  let joined = $state<Record<string, { name: string; joinedAt: string; role?: "member" | "treasurer"; userId?: string }>>({});
  let invited = $state<Set<string>>(new Set());

  async function loadMembers() {
    if (!app.groupId || app.memberSession) return;
    try {
      const r = await api<{ members: { personId: string; name: string; joinedAt: string; role?: "member" | "treasurer"; userId?: string }[]; invited: { personId: string }[] }>(
        `/api/groups/${app.groupId}/members`,
      );
      joined = Object.fromEntries(r.members.map((m) => [m.personId, m]));
      invited = new Set(r.invited.map((i) => i.personId));
    } catch {
      /* offline: badges just don't show */
    }
  }
  $effect(() => {
    void app.groupId;
    void loadMembers();
  });

  const netError = (e: unknown) =>
    e instanceof NetworkError ? "That needs a connection. Try again when you're online." : String((e as Error).message);

  async function invite(p: PersonSummary) {
    try {
      const { url } = await api<{ url: string }>(`/api/groups/${app.groupId}/view-links`, { body: { personId: p.personId } });
      invited = new Set([...invited, p.personId]);
      const text = `Join ${groupName} on Split Ledger to see your items and what you owe: ${url}`;
      if (navigator.share) {
        await navigator.share({ title: `Join ${groupName}`, text, url }).catch(() => {});
      } else {
        await navigator.clipboard.writeText(url);
        app.toast(`Copied ${p.name}'s invite link. It works once, for them.`);
      }
    } catch (e) {
      app.toast(netError(e), "error");
    }
  }

  /** Roles: you see everything; a club treasurer runs this group; a member sees their own. */
  async function setRole(p: PersonSummary, role: "member" | "treasurer") {
    try {
      await api(`/api/groups/${app.groupId}/members/${p.personId}/role`, { method: "PUT", body: { role } });
      app.toast(role === "treasurer" ? `${p.name} now runs ${groupName} with you` : `${p.name} sees only their own items again`);
      await loadMembers();
    } catch (e) {
      app.toast(netError(e), "error");
    }
  }

  async function removeAccess(p: PersonSummary) {
    try {
      await api(`/api/groups/${app.groupId}/members/${p.personId}`, { method: "DELETE", body: {} });
      app.toast(`${p.name} can no longer see this group`);
      await loadMembers();
    } catch (e) {
      app.toast(netError(e), "error");
    }
  }


  const groupName = $derived(app.groups.find((g) => g.id === app.groupId)?.name ?? S.name);

  // ---- which groups each account sees (yours only): tick a group to put them in it
  type Account = { id: string; name: string; role: string; groups: { groupId: string }[] };
  let accounts = $state<Account[]>([]);
  async function loadAccounts() {
    if (!nav.isOwner || app.memberSession) return;
    try {
      accounts = (await api<{ accounts: Account[] }>("/api/admin/accounts")).accounts.filter((a) => a.role === "member");
    } catch {
      /* offline: the lists just don't show */
    }
  }
  $effect(() => {
    void app.groupId;
    void loadAccounts();
  });
  const groupsOf = (userId: string) => new Set(accounts.find((a) => a.id === userId)?.groups.map((g) => g.groupId) ?? []);
  const assignable = $derived(app.groups.filter((g) => !isTestsGroup(g.name)));
  const unlinked = $derived(accounts.filter((a) => !a.groups.some((g) => g.groupId === app.groupId)));
  let busyGroup = $state<string | null>(null);

  async function setInGroup(userId: string, who: string, g: { id: string; name: string }, on: boolean) {
    busyGroup = g.id;
    try {
      const r = await api<{ added?: boolean }>(`/api/admin/accounts/${userId}/groups`, { body: { groupId: g.id, in: on } });
      app.toast(on ? (r.added ? `${who} is in ${g.name} now, added as a new person` : `${who} is in ${g.name} now`) : `${who} no longer sees ${g.name}`);
      await Promise.all([loadAccounts(), loadMembers()]);
      app.syncEverything();
    } catch (e) {
      app.toast(netError(e), "error");
    } finally {
      busyGroup = null;
    }
  }

  let linkPick = $state<Record<string, string>>({});
  async function linkAccount(p: PersonSummary) {
    const id = linkPick[p.personId];
    const a = accounts.find((x) => x.id === id);
    if (!a) return;
    try {
      await api(`/api/admin/accounts/${a.id}/link`, { body: { groupId: app.groupId, personId: p.personId } });
      app.toast(`${a.name}'s account is ${p.name} in ${groupName} now`);
      await Promise.all([loadAccounts(), loadMembers()]);
    } catch (e) {
      app.toast(netError(e), "error");
    }
  }

  // ---- editing someone: full name (first and last) and color, right here on People
  const hasLastName = (n: string) => tidyName(n).split(" ").length >= 2;
  let editingPerson = $state<string | null>(null);
  let editName = $state("");
  let editColor = $state("");
  function startEdit(id: string) {
    editingPerson = id;
    editName = S.people[id]?.name ?? "";
    editColor = S.people[id]?.color ?? "Gray";
  }
  async function saveEdit(id: string) {
    const name = tidyName(editName);
    const before = S.people[id];
    if (!before || !hasLastName(name)) return;
    const patch: { name?: string; color?: string } = {};
    if (name !== before.name) patch.name = name;
    if (editColor !== before.color) patch.color = editColor;
    const err = Object.keys(patch).length ? await app.do_({ type: "person.update", personId: id, patch }, "Saved") : null;
    if (!err) editingPerson = null;
  }
  async function archive(id: string) {
    const err = await app.do_({ type: "person.update", personId: id, patch: { archived: true } }, "Archived. Restore them in the group's settings.");
    if (!err) editingPerson = null;
  }

  async function addPerson(e: SubmitEvent) {
    e.preventDefault();
    const name = tidyName(newName);
    if (!hasLastName(name)) {
      app.toast("Add their last name too.", "error");
      return;
    }
    const err = await app.do_({ type: "person.add", personId: crypto.randomUUID(), name, color: newColor }, `Added ${name}`);
    if (!err) newName = "";
  }

  // A small balance-over-time line for each row, last 16 weeks (fills the row's empty middle).
  const sparkWeeks = $derived.by(() => {
    const d = new Date(`${app.today}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 7 * 15);
    const start = firstActivity(S, L);
    const from = d.toISOString().slice(0, 10);
    return trendDates(start && start > from ? start : from, app.today);
  });
  const sparks = $derived.by(() => {
    const out: Record<string, number[]> = {};
    for (const p of visiblePeople.slice(0, 80)) {
      if (p.status === "you" || p.totalMicro === 0) continue;
      out[p.personId] = owedTrend(S, L, sparkWeeks, p.personId).map((t) => t.valueMicro);
    }
    return out;
  });
</script>

<div class="page">
  <div class="page-head">
    <div>
      <h1>People</h1>
      {#if !app.memberSession}
        <p class="lede">
          {#if owing.length || owed.length}
            {fill(A.TOTALS_LINE, { owedLabel: A.TOTAL_OWED_LABEL, owed: formatCents(owedSum), oweLabel: A.TOTAL_OWE_LABEL, owe: formatCents(oweSum) })}
          {:else}
            {A.TOTALS_SQUARE}
          {/if}
        </p>
      {/if}
    </div>
    {#if app.canEdit}<button class="btn primary" onclick={() => (paying = { personId: "", amountCents: 0 })}>Log payment</button>{/if}
  </div>


  {#if L.people.length > 8}
    <div class="ptools">
      <input class="input" type="search" bind:value={q} placeholder="Search people" aria-label="Search people" />
      <select class="input" bind:value={sort} aria-label="Sort people">
        <option value="order">Group order</option>
        <option value="owes">Owes the most</option>
        <option value="name">Name</option>
      </select>
    </div>
  {/if}

  <div class="list">
    {#each visiblePeople as p (p.personId)}
      {@const acct = joined[p.personId]}
      <PersonCard
        overview={overviewOf(p)}
        name={p.name}
        scope="group"
        viewer={viewerOf(p)}
        open={open === p.personId}
        ontoggle={() => (open = open === p.personId ? null : p.personId)}
        color={p.color}
        status={paidStatus(p)}
        progress={p.totalMicro > 0 && p.status !== "you" ? progress(p) : null}
        spark={sparks[p.personId] ?? null}
        message={!app.memberSession ? reminderMessage(p) : ""}
        isYou={p.status === "you"}
        onentry={app.canEdit ? openEntry : null}
      >
        {#snippet badges()}
          {#if acct?.role === "treasurer"}<span class="badge ok">{T.BADGE_TREASURER}</span>{:else if acct}<span class="badge ok">{T.BADGE_ACCOUNT}</span>{:else if invited.has(p.personId)}<span class="badge">{T.BADGE_INVITED}</span>{/if}
        {/snippet}
        {#snippet actions()}
          {#if editingPerson === p.personId}
            <form class="edit" onsubmit={(e) => { e.preventDefault(); void saveEdit(p.personId); }}>
              <FullName bind:value={editName} label={T.EDIT_NAME_LABEL} />
              <label class="field">
                <span>{T.EDIT_COLOR_LABEL}</span>
                <span class="color-row">
                  <select class="input" bind:value={editColor}>
                    {#each PALETTE as c}<option value={c}>{c}</option>{/each}
                    {#if editColor.startsWith("#")}<option value={editColor}>{editColor}</option>{/if}
                  </select>
                  <input class="hex" type="color" value={editColor.startsWith("#") ? editColor : "#3b7ddd"} onchange={(e) => (editColor = (e.currentTarget as HTMLInputElement).value)} aria-label={T.EDIT_ANY_COLOR} title={T.EDIT_ANY_COLOR} />
                </span>
              </label>
              {#if !hasLastName(editName)}<p class="owes small">{T.EDIT_NEED_LAST_NAME}</p>{/if}
              <div class="actions">
                <button class="btn small primary" disabled={!hasLastName(editName)}>{T.EDIT_SAVE}</button>
                <button type="button" class="btn small quiet" onclick={() => (editingPerson = null)}>{T.EDIT_CANCEL}</button>
                {#if p.status !== "you"}<button type="button" class="btn small quiet danger" onclick={() => archive(p.personId)}>{T.EDIT_ARCHIVE}</button>{/if}
              </div>
            </form>
          {/if}
          {#if !app.memberSession}
            <div class="actions">
              {#if app.canEdit && editingPerson !== p.personId}
                <button class="btn small" onclick={() => startEdit(p.personId)}>{T.ACT_EDIT}</button>
              {/if}
              {#if p.status !== "you"}
                <button class="btn small primary" disabled={app.readOnly} onclick={() => (paying = { personId: p.personId, amountCents: p.status === "owes" ? Math.round(p.balanceMicro / 10_000) : 0 })}>{T.ACT_LOG_PAYMENT}</button>
                {#if reminderMessage(p)}<button class="btn small" onclick={() => copy(p)}>{T.ACT_COPY_MESSAGE}</button>{/if}
                {#if acct}
                  <button class="btn small" onclick={() => { const gid = app.groupId; app.setViewAsMember(true, p.personId); if (gid) router.go(groupPath(gid)); }}>{T.ACT_SEE_AS}</button>
                  {#if acct.role === "treasurer"}
                    <button class="btn small" onclick={() => setRole(p, "member")}>{T.ACT_MAKE_MEMBER}</button>
                  {:else}
                    <button class="btn small" onclick={() => setRole(p, "treasurer")} title={T.ACT_MAKE_TREASURER_TITLE}>{T.ACT_MAKE_TREASURER}</button>
                  {/if}
                  <button class="btn small quiet danger" onclick={() => removeAccess(p)}>{T.ACT_REMOVE_ACCESS}</button>
                {:else}
                  <button class="btn small" onclick={() => invite(p)}>{invited.has(p.personId) ? T.ACT_NEW_INVITE : T.ACT_INVITE}</button>
                {/if}
              {/if}
            </div>
          {/if}
        {/snippet}
        {#snippet extra()}
          {#if nav.isOwner && !app.memberSession && p.status !== "you"}
            {#if acct?.userId}
              {@const ins = groupsOf(acct.userId)}
              {@const title = fill(T.ACCT_GROUPS_TITLE, { name: acct.name })}
              <h3 class="hist-h">{title}</h3>
              <div class="acct-groups" role="group" aria-label={title}>
                {#each assignable as g (g.id)}
                  <label class="acct-g">
                    <input type="checkbox" checked={ins.has(g.id)} disabled={busyGroup !== null} onchange={(e) => setInGroup(acct.userId!, acct.name, g, (e.currentTarget as HTMLInputElement).checked)} />{g.name}{#if busyGroup === g.id}<span class="muted small">{T.ACCT_SAVING}</span>{/if}
                  </label>
                {/each}
              </div>
              {#if shows(T.ACCT_GROUPS_NOTE)}<p class="muted small">{T.ACCT_GROUPS_NOTE}</p>{/if}
            {:else if !acct && unlinked.length}
              <h3 class="hist-h">{T.ACCT_TITLE}</h3>
              <div class="acct-link">
                <select class="input" aria-label={fill(T.ACCT_FOR_LABEL, { name: p.name })} bind:value={linkPick[p.personId]}>
                  <option value="">{T.ACCT_CHOOSE}</option>
                  {#each unlinked as a (a.id)}<option value={a.id}>{a.name}</option>{/each}
                </select>
                <button class="btn small" disabled={!linkPick[p.personId]} onclick={() => linkAccount(p)}>{T.ACCT_LINK}</button>
              </div>
            {/if}
          {/if}
        {/snippet}
      </PersonCard>
    {:else}
      <div class="empty">Add the people you split with.</div>
    {/each}
  </div>

  {#if app.canEdit}
    <form class="add" onsubmit={addPerson}>
      <div class="field grow">
        <span>Add a person</span>
        <FullName bind:value={newName} label="Add a person" />
      </div>
      <label class="field">
        <span>Color</span>
        <select class="input" bind:value={newColor}>
          {#each freeColors.length ? freeColors : PALETTE as c}<option value={c}>{c}</option>{/each}
          {#if newColor.startsWith("#")}<option value={newColor}>{newColor}</option>{/if}
        </select>
        <input class="hex" type="color" value={newColor.startsWith("#") ? newColor : "#3b7ddd"} onchange={(e) => (newColor = (e.currentTarget as HTMLInputElement).value)} aria-label="Any color" title="Any color" />
      </label>
      <button class="btn primary" disabled={!hasLastName(newName)}>Add</button>
    </form>
  {/if}
</div>


{#if paying}
  <PaymentEditor personId={paying.personId} amountCents={paying.amountCents} onclose={() => (paying = null)} />
{/if}
{#if editingPayment}
  <PaymentEditor payment={editingPayment} onclose={() => (editingPayment = null)} />
{/if}

<style>
  .edit {
    display: grid;
    gap: 10px;
    margin: 6px 0 10px;
    padding: 12px;
    border-radius: var(--r-md);
    background: var(--wash);
  }

  .edit p {
    margin: 0;
  }

  .color-row {
    display: flex;
    align-items: center;
  }

  .hex {
    width: 34px;
    height: 34px;
    margin-left: 6px;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    vertical-align: middle;
  }



  .acct-groups {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    margin: 2px 0 4px;
  }

  .acct-g {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    padding: 4px 0;
  }

  .acct-link {
    display: flex;
    gap: 8px;
    align-items: center;
    max-width: 420px;
  }

  .hist-h {
    margin: 14px 0 4px;
    font-size: var(--t-sm);
  }

  .ptools {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  .ptools .input[type="search"] {
    flex: 1 1 240px;
    max-width: 380px;
  }

  .ptools select {
    width: auto;
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .add {
    margin-top: 20px;
    display: flex;
    gap: 10px;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .grow {
    flex: 1 1 240px;
  }

</style>
